import React, { useState } from 'react';
import axios from 'axios';
import { Maximize2, Minimize2 } from 'lucide-react';
import { useNotifications } from '../../hooks/useNotifications';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useExpandable } from '../../hooks/useExpandable';

interface PagamentoItem {
  _id: string;
  numeroParcela: number;
  totalParcelas: number;
  valor: number;
  vencimento: string;
  status: 'pendente' | 'pago' | 'atrasado' | 'cancelado';
  dataPagamento?: string | null;
  formaPagamentoRecebido?: string;
  observacoes?: string;
  clienteId?: { _id: string; nome: string };
  vendaId?: { _id: string; descricao: string };
}

interface EditParcelaModalProps {
  pagamento: PagamentoItem;
  onClose: () => void;
  onSaved: (updated: PagamentoItem) => void;
}

const toDateInput = (value?: string | null) => (value ? value.slice(0, 10) : '');

const EditParcelaModal: React.FC<EditParcelaModalProps> = ({ pagamento, onClose, onSaved }) => {
  const notifications = useNotifications();
  const [valor, setValor] = useState(String(pagamento.valor));
  const [vencimento, setVencimento] = useState(toDateInput(pagamento.vencimento));
  const [observacoes, setObservacoes] = useState(pagamento.observacoes || '');
  const [status, setStatus] = useState(pagamento.status);
  const [dataPagamento, setDataPagamento] = useState(toDateInput(pagamento.dataPagamento));
  const [formaPagamentoRecebido, setFormaPagamentoRecebido] = useState(pagamento.formaPagamentoRecebido || '');
  const [saving, setSaving] = useState(false);
  const { isExpanded, toggleExpanded } = useExpandable();
  useEscapeKey(onClose, true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!valor || Number(valor) < 0) {
      notifications.warning.validation('Informe um valor válido.');
      return;
    }
    if (!vencimento) {
      notifications.warning.validation('Informe a data de vencimento.');
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        valor: Number(valor),
        vencimento,
        observacoes,
        status
      };
      if (status === 'pago') {
        payload.dataPagamento = dataPagamento || undefined;
        payload.formaPagamentoRecebido = formaPagamentoRecebido;
      }

      const response = await axios.put(`/pagamentos/${pagamento._id}`, payload);
      onSaved(response.data.pagamento);
      notifications.success.pagamentoAtualizado();
      if (response.data.avisoValorTotal) {
        notifications.warning.validation(response.data.avisoValorTotal);
      }
      onClose();
    } catch (error: any) {
      console.error('Erro ao editar pagamento:', error);
      notifications.error.pagamentoAction(error.response?.data?.message || 'Erro ao editar parcela');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className={`bg-white rounded-t-2xl sm:rounded-lg shadow-xl w-full overflow-y-auto transition-all duration-200 ${
        isExpanded ? 'max-w-[95vw] max-h-[95vh]' : 'sm:max-w-md max-h-[90vh]'
      }`}>
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              Editar parcela {pagamento.numeroParcela}/{pagamento.totalParcelas}
            </h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={toggleExpanded}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                title={isExpanded ? 'Restaurar' : 'Expandir'}
              >
                {isExpanded ? <Minimize2 className="w-5 h-5 sm:w-6 sm:h-6" /> : <Maximize2 className="w-5 h-5 sm:w-6 sm:h-6" />}
              </button>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="px-4 sm:px-6 py-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Valor (€)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Vencimento</label>
              <input
                type="date"
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as PagamentoItem['status'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="pendente">Pendente</option>
                <option value="pago">Pago</option>
                <option value="atrasado">Atrasado</option>
                <option value="cancelado">Cancelado</option>
              </select>
              {pagamento.status === 'pago' && status !== 'pago' && (
                <p className="text-xs text-amber-600 mt-1">
                  Ao sair de "Pago", a data de pagamento e a forma de recebimento serão limpas.
                </p>
              )}
            </div>

            {status === 'pago' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data do pagamento</label>
                  <input
                    type="date"
                    value={dataPagamento}
                    onChange={(e) => setDataPagamento(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Forma de pagamento recebido</label>
                  <input
                    type="text"
                    value={formaPagamentoRecebido}
                    onChange={(e) => setFormaPagamentoRecebido(e.target.value)}
                    placeholder="Ex: Transferência, cartão..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                value={observacoes}
                onChange={(e) => setObservacoes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditParcelaModal;
